import React, { useState } from 'react';
import { Steps, Form, Input, Button, Select, Card, Radio, Checkbox, message, Divider } from 'antd';
import { 
  UserOutlined, 
  IdcardOutlined, 
  SafetyOutlined, 
  SolutionOutlined,
  BookOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons';
import './KYCVerification.css';

const { Step } = Steps;
const { Option } = Select;

const KYCVerification = ({ userType }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Tanzanian education system grade levels
  const tanzaniaGradeLevels = [
    'Baby Class',
    'Kindergarten 1 (KG1)',
    'Kindergarten 2 (KG2)',
    'Standard 1',
    'Standard 2',
    'Standard 3',
    'Standard 4',
    'Standard 5',
    'Standard 6',
    'Standard 7',
    'Form 1',
    'Form 2',
    'Form 3',
    'Form 4',
    'Form 5',
    'Form 6',
    'University/College'
  ];

  // Different question sets for parents and students
  const steps = {
    parent: [
      {
        title: 'Personal Details',
        icon: <UserOutlined />,
        questions: [
          {
            name: 'fullName',
            label: 'Full Legal Name',
            type: 'text',
            rules: [{ required: true, message: 'Please enter your full name' }],
            icon: <UserOutlined />
          },
          {
            name: 'relationship',
            label: 'Relationship to Student',
            type: 'select',
            options: ['Father', 'Mother', 'Guardian', 'Other Relative'],
            rules: [{ required: true, message: 'Please select relationship' }],
            icon: <SolutionOutlined />
          },
          {
            name: 'idType',
            label: 'Identification Type',
            type: 'select',
            options: ['National ID', 'Passport', 'Driving License', 'Voter ID'],
            rules: [{ required: true, message: 'Please select ID type' }],
            icon: <IdcardOutlined />
          },
          {
            name: 'idNumber',
            label: 'Identification Number',
            type: 'text',
            rules: [{ required: true, message: 'Please enter ID number' }],
            icon: <IdcardOutlined />
          }
        ]
      },
      {
        title: 'Contact Information',
        icon: <PhoneOutlined />,
        questions: [
          {
            name: 'phoneNumber',
            label: 'Mobile Number',
            type: 'text',
            rules: [
              { required: true, message: 'Please enter your phone number' },
              { pattern: /^[0-9]{10,15}$/, message: 'Please enter a valid phone number' }
            ],
            icon: <PhoneOutlined />
          },
          {
            name: 'email',
            label: 'Email Address',
            type: 'text',
            rules: [
              { type: 'email', message: 'Please enter a valid email' }
            ],
            icon: <MailOutlined />
          }
        ]
      },
      {
        title: 'Verification',
        icon: <SafetyOutlined />,
        questions: [
          {
            name: 'consent',
            label: 'I certify that all information provided is accurate and agree to the terms of service',
            type: 'checkbox',
            rules: [{ required: true, message: 'You must agree to continue' }]
          }
        ]
      }
    ],
    student: [
      {
        title: 'Student Information',
        icon: <UserOutlined />,
        questions: [
          {
            name: 'fullName',
            label: 'Full Legal Name',
            type: 'text',
            rules: [{ required: true, message: 'Please enter your full name' }],
            icon: <UserOutlined />
          },
          {
            name: 'birthDate',
            label: 'Date of Birth',
            type: 'date',
            rules: [{ required: true, message: 'Please select date of birth' }],
            icon: <SolutionOutlined />
          },
          {
            name: 'gradeLevel',
            label: 'Current Grade Level',
            type: 'select',
            options: tanzaniaGradeLevels,
            rules: [{ required: true, message: 'Please select grade level' }],
            icon: <BookOutlined />
          }
        ]
      },
      {
        title: 'Parent/Guardian Details',
        icon: <SolutionOutlined />,
        questions: [
          {
            name: 'parentName',
            label: 'Parent/Guardian Full Name',
            type: 'text',
            rules: [{ required: true, message: 'Please enter parent/guardian name' }],
            icon: <UserOutlined />
          },
          {
            name: 'parentPhone',
            label: 'Parent/Guardian Phone',
            type: 'text',
            rules: [
              { required: true, message: 'Please enter contact number' },
              { pattern: /^[0-9]{10,15}$/, message: 'Please enter a valid phone number' }
            ],
            icon: <PhoneOutlined />
          }
        ]
      },
      {
        title: 'Verification',
        icon: <SafetyOutlined />,
        questions: [
          {
            name: 'consent',
            label: 'I certify that all information provided is accurate (Parent/Guardian must verify)',
            type: 'checkbox',
            rules: [{ required: true, message: 'You must agree to continue' }]
          }
        ]
      }
    ]
  };

  const currentSteps = steps[userType] || steps.student;

  const nextStep = () => {
    form.validateFields()
      .then(() => {
        setCurrentStep(currentStep + 1);
      })
      .catch(err => {
        console.log('Validation Error:', err);
      });
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      console.log('KYC Submitted:', values);
      // Here you would typically send the data to your backend
      // await submitKYC(values);
      message.success('KYC verification submitted successfully!');
      // Redirect to dashboard or next step
    } catch (error) {
      console.error('Submission Error:', error);
      message.error('Failed to submit KYC. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = (question) => {
    switch (question.type) {
      case 'text':
        return (
          <Form.Item
            name={question.name}
            label={<span className="form-label">{question.label}</span>}
            rules={question.rules}
          >
            <Input 
              prefix={question.icon} 
              placeholder={`Enter ${question.label.toLowerCase()}`}
              className="modern-input"
            />
          </Form.Item>
        );
      case 'select':
        return (
          <Form.Item
            name={question.name}
            label={<span className="form-label">{question.label}</span>}
            rules={question.rules}
          >
            <Select 
              placeholder={`Select ${question.label.toLowerCase()}`}
              className="modern-select"
              suffixIcon={question.icon}
            >
              {question.options.map(option => (
                <Option key={option} value={option}>{option}</Option>
              ))}
            </Select>
          </Form.Item>
        );
      case 'date':
        return (
          <Form.Item
            name={question.name}
            label={<span className="form-label">{question.label}</span>}
            rules={question.rules}
          >
            <Input 
              type="date"
              prefix={question.icon}
              className="modern-input"
            />
          </Form.Item>
        );
      case 'checkbox':
        return (
          <Form.Item
            name={question.name}
            valuePropName="checked"
            rules={question.rules}
          >
            <Checkbox className="modern-checkbox">
              <span className="consent-text">{question.label}</span>
            </Checkbox>
          </Form.Item>
        );
      default:
        return null;
    }
  };

  return (
    <div className="kyc-verification-container">
      <div className="kyc-header">
        <h2>{userType === 'parent' ? 'Parent' : 'Student'} Verification</h2>
        <p className="subtitle">Complete your profile to access all platform features</p>
      </div>
      
      <Card className="kyc-card">
        <Steps current={currentStep} className="kyc-steps">
          {currentSteps.map((step, index) => (
            <Step 
              key={index} 
              title={step.title} 
              icon={step.icon} 
              className={currentStep === index ? 'active-step' : ''}
            />
          ))}
        </Steps>

        <Divider className="divider" />

        <div className="kyc-form-container">
          <Form
            form={form}
            layout="vertical"
            className="kyc-form"
            initialValues={{}}
          >
            <div className="step-content">
              <h3 className="step-title">
                {currentSteps[currentStep].icon}
                <span style={{ marginLeft: 8 }}>{currentSteps[currentStep].title}</span>
              </h3>
              {currentSteps[currentStep].questions.map((question, index) => (
                <div key={index} className="question-item">
                  {renderQuestion(question)}
                </div>
              ))}
            </div>

            <div className="form-actions">
              {currentStep > 0 && (
                <Button 
                  onClick={prevStep} 
                  className="action-button secondary"
                >
                  Back
                </Button>
              )}
              {currentStep < currentSteps.length - 1 ? (
                <Button 
                  type="primary" 
                  onClick={nextStep}
                  className="action-button primary"
                >
                  Continue
                </Button>
              ) : (
                <Button 
                  type="primary" 
                  onClick={handleSubmit}
                  loading={loading}
                  className="action-button submit"
                >
                  Complete Verification
                </Button>
              )}
            </div>
          </Form>
        </div>
      </Card>
    </div>
  );
};

export default KYCVerification;